import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";

interface VideoModalProps {
  videoUrl?: string;
  triggerText?: React.ReactNode;
  className?: string;
  showYoutubeIcon?: boolean;
}

const formatYouTubeEmbedUrl = (url?: string): string => {
  if (!url) return "https://www.youtube.com/embed/OUB6zKc-Tos?autoplay=1";

  if (url.includes("embed/")) {
    return url.includes("autoplay=") ? url : `${url}${url.includes("?") ? "&" : "?"}autoplay=1`;
  }

  let videoId = "";
  if (url.includes("youtu.be/")) {
    videoId = url.split("youtu.be/")[1]?.split("?")[0]?.split("&")[0] || "";
  } else if (url.includes("watch?v=")) {
    videoId = url.split("watch?v=")[1]?.split("&")[0] || "";
  }

  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
  }

  return url;
};

const YouTubePlayIcon: React.FC = () => (
  <svg
    width="24"
    height="17"
    viewBox="0 0 24 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}
    aria-hidden="true"
  >
    <path
      d="M23.498 2.628a3.016 3.016 0 0 0-2.122-2.136C19.505 0 12 0 12 0s-7.505 0-9.377.492A3.016 3.016 0 0 0 .502 2.628C0 4.514 0 8.45 0 8.45s0 3.936.502 5.822a3.016 3.016 0 0 0 2.122 2.136C4.495 16.9 12 16.9 12 16.9s7.505 0 9.377-.492a3.016 3.016 0 0 0 2.122-2.136C24 12.386 24 8.45 24 8.45s0-3.936-.502-5.822z"
      fill="#FF0000"
    />
    <path d="M9.545 12.068V4.832L15.818 8.45l-6.273 3.618z" fill="#FFFFFF" />
  </svg>
);

const VideoModal: React.FC<VideoModalProps> = ({
  videoUrl = "https://www.youtube.com/embed/OUB6zKc-Tos?autoplay=1",
  triggerText,
  className,
  showYoutubeIcon = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const embedUrl = formatYouTubeEmbedUrl(videoUrl);

  const openModal = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setIsOpen(true);
    setTimeout(() => {
      if (iframeRef.current) iframeRef.current.src = embedUrl;
    }, 50);
  };

  const closeModal = () => {
    if (iframeRef.current) iframeRef.current.src = "about:blank"; // stop video
    setIsOpen(false);
  };

  const modalContent = isOpen ? (
    <div
      role="dialog"
      aria-modal="true"
      className="video-popup-overlay"
      onClick={closeModal}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0, 2, 15, 0.65)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 999999,
        padding: "20px",
        boxSizing: "border-box",
      }}
    >
      <div
        className="video-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "min(1150px, 92vw)",
          maxWidth: "100%",
          maxHeight: "85vh",
          aspectRatio: "16 / 9",
          background: "#00020f",
          borderRadius: 16,
          border: "2px solid rgba(255, 255, 255, 0.4)",
          boxShadow: "0 0 35px rgba(255, 255, 255, 0.12), 0 25px 70px rgba(0,0,0,0.8)",
          overflow: "hidden",
          boxSizing: "border-box",
          animation: "fadeIn 0.3s ease",
        }}
      >
        <iframe
          ref={iframeRef}
          title="Ai Video"
          width="100%"
          height="100%"
          src="about:blank"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          frameBorder={0}
        />
        <button
          onClick={closeModal}
          aria-label="Close video"
          style={{
            position: "absolute",
            top: 14,
            right: 16,
            color: "#ffffff",
            background: "rgba(0, 2, 15, 0.75)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            borderRadius: "50%",
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            cursor: "pointer",
            lineHeight: 1,
            zIndex: 10,
            transition: "all 0.2s ease",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
            e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.6)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(0, 2, 15, 0.75)";
            e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.3)";
          }}
        >
          ✕
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      {/* ▶️ Trigger Button / Link */}
      {triggerText ? (
        <a
          href={videoUrl}
          onClick={openModal}
          className={`xb-video-tutorial-link ${className || ""}`}
          style={{
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "9px",
            fontSize: "15px",
            fontWeight: 500,
            color: "#00d2ff",
            textDecoration: "none",
            transition: "all 0.3s ease",
          }}
        >
          {showYoutubeIcon ? <YouTubePlayIcon /> : <i className="fi flaticon-play" aria-hidden="true" style={{ fontSize: "14px" }}></i>}
          <span>{triggerText}</span>
        </a>
      ) : (
        <button className="video-btn" onClick={openModal} aria-label="Play video">
          <i className="fi flaticon-play" aria-hidden="true"></i>
        </button>
      )}

      {/* 🎬 Portal Video Modal (mounted on document.body) */}
      {typeof document !== "undefined" && modalContent ? createPortal(modalContent, document.body) : null}
    </>
  );
};

export default VideoModal;
